import {
    Box,
    BoxProps,
    Tooltip,
    TooltipProps,
    Typography,
    useTheme,
} from '@mui/material';
import { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import ApprovalMark from './ApprovalMark';
import { approvalStatusMark } from './approvalStatusMark';

export default function ReviewStatus({
    status,
    fontSize = 14,
    fontWeight = 400,
    boxProps,
    color,
    onClick,
    tooltip,
    tooltipPlacement = 'top',
    tooltipAnchorEl,
    dataTestId,
}: {
    status: DICOMApprovalStatus;
    fontSize?: number;
    fontWeight?: number;
    boxProps?: BoxProps;
    color?: string;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
    tooltip?: ReactNode;
    /** Which way it opens. */
    tooltipPlacement?: TooltipProps['placement'];
    /** Anchor to position against, for a box that must clear the whole row. */
    tooltipAnchorEl?: HTMLElement | null;
    dataTestId?: string;
}) {
    const { palette } = useTheme();

    const { labelId } = approvalStatusMark(status);

    const isClickable = Boolean(onClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
        }
    };

    const { sx: boxSx, ...restBoxProps } = boxProps ?? {};

    const content = (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            onClick={onClick}
            onKeyDown={isClickable ? handleKeyDown : undefined}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            data-testid={dataTestId}
            {...restBoxProps}
            sx={[
                isClickable && {
                    cursor: 'pointer',
                    borderRadius: 0.5,
                    px: 0.5,
                    mx: -0.5,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${palette.primary.main}`,
                        outlineOffset: 1,
                    },
                },
                ...(Array.isArray(boxSx) ? boxSx : boxSx ? [boxSx] : []),
            ]}
        >
            <ApprovalMark status={status} size={fontSize} />
            <Typography
                fontSize={fontSize}
                fontWeight={fontWeight}
                lineHeight={1.5}
                color={color}
            >
                <FormattedMessage id={labelId} />
            </Typography>
        </Box>
    );

    if (tooltip) {
        return (
            <Tooltip
                title={tooltip}
                placement={tooltipPlacement}
                PopperProps={
                    tooltipAnchorEl ? { anchorEl: tooltipAnchorEl } : undefined
                }
            >
                {content}
            </Tooltip>
        );
    }

    return content;
}
